"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowRight, Award, Download, ShieldCheck, Star, Users } from "lucide-react";
import { Section } from "@/components/section";
import { Heading } from "@/components/ui/heading";
import Image from "next/image";
import { NUMBER_OF_LEARNERS } from "@/lib/site-config";
import { Text } from "@/components/ui/text";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { TrustedBy } from "@/components/common/trusted-by";
import { Suspense } from "react";
import { AccreditationMarquee } from "@/components/common/accreditation-marquee";
import { Avatar, AvatarFallback, AvatarGroup, AvatarGroupCount } from "@/components/ui/avatar";
import { CallToAction } from "@/components/common/call-to-action";
import { Rating } from "@/components/ui/rating";
import { usePopup } from "@/providers/popup-provider";
import { AccreditationMarqueeLoader } from "@/components/loadings/accreditation-marquee-loader";
import { useCountry } from "@/hooks/use-country";
type BannerProps = {
  section: PageSections;
  data: {
    heading: string;
    paragraph: string;
    imageAltText: string;
    bannerImage: string;
    backgroundBannerImage?: string;
    categories: {
      categoryName: string;
    }[];
  };
};
const Banner = t0 => {
  const $ = _c(37);
  const {
    data,
    section
  } = t0;
  const country = useCountry();
  const {
    openPopup
  } = usePopup();
  const t1 = section.sectionId;
  const t2 = `${section.sectionClass} to-background dark:from-background relative overflow-hidden bg-linear-to-b from-indigo-50`;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Badge><Star size={10} fill="currentColor" /> Pro Learning</Badge>;
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex items-center gap-3">{t3}<span className="flex items-center gap-1.5 text-xs font-semibold text-green-700"><ShieldCheck size={14} /> 100% Money-Back Guarantee</span></div>;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== data.heading) {
    t5 = <Heading as="h1" forceAs={true}>{data.heading}</Heading>;
    $[2] = data.heading;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== data.paragraph) {
    t6 = <Text>{data.paragraph}</Text>;
    $[4] = data.paragraph;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ArrowRight />;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const t8 = `/${country ? `${country}/courses` : "courses"}`;
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Download />;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== openPopup) {
    t10 = () => openPopup({
      type: "download-brochure"
    });
    $[8] = openPopup;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== t10 || $[11] !== t8) {
    t11 = <CallToAction size="lg" onSaveText="Explore All Courses" onSaveIcon={t7} onSaveLink={t8} onCancelText="Download Brochure" onCancelIcon={t9} onCancel={t10} />;
    $[10] = t10;
    $[11] = t8;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = ["https://cdn.spoclearn.com/testimonials/01K2EVSQ9WYZ13G52PF651GPTA.jpg", "https://cdn.spoclearn.com/testimonials/01K2EVFQVZN74VVAAX2GNTQT0B.jpg", "https://cdn.spoclearn.com/testimonials/01KRMWRHS20EV4BPF28CXKD0H6.jpg", "https://cdn.spoclearn.com/testimonials/01KRMWJFPAZ33YDS8HHD5NV5RF.jpg"].map(_temp);
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <AvatarGroup>{t12}<AvatarGroupCount className="bg-primary text-primary-foreground size-8 text-xs sm:size-10 sm:text-sm">{NUMBER_OF_LEARNERS?.split(",")[0]}k+</AvatarGroupCount></AvatarGroup>;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="col-span-2 flex flex-wrap items-start gap-4 pt-2 sm:items-center">{t13}<div className="flex flex-col gap-1"><Rating rating={4.7} outOf={5} /><p className="text-xs font-medium">Rated by {NUMBER_OF_LEARNERS} Happy Learners</p></div></div>;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="flex items-center gap-2"><div className="text-primary flex h-10 w-10 items-center justify-center rounded-full bg-blue-100"><Award size={20} /></div><div><p className="text-xs font-bold">Industry Certified</p><p className="text-muted-foreground text-[10px]">Global Recognition</p></div></div>;
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  let t16;
  let t17;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="grid grid-cols-1 items-start justify-between gap-2 sm:grid-cols-[auto]">{t14}<div className="flex flex-wrap gap-x-8 gap-y-4 pt-2">{t15}<div className="flex items-center gap-2"><div className="text-secondary flex h-10 w-10 items-center justify-center rounded-full bg-orange-100"><Users size={20} /></div><div><p className="text-xs font-bold">Expert Mentors</p><p className="text-muted-foreground text-[10px]">1-on-1 Support</p></div></div></div></div>;
    t17 = <TrustedBy />;
    $[17] = t16;
    $[18] = t17;
  } else {
    t16 = $[17];
    t17 = $[18];
  }
  let t18;
  if ($[19] !== t11 || $[20] !== t5 || $[21] !== t6) {
    t18 = <div className="col-span-12 md:col-span-7"><div className="relative z-10 space-y-8">{t4}{t5}{t6}{t11}{t16}{t17}</div></div>;
    $[19] = t11;
    $[20] = t5;
    $[21] = t6;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  if ($[23] !== data.bannerImage || $[24] !== data.imageAltText) {
    t19 = data.bannerImage && <div className="relative w-full max-w-119.5"><div className="bg-card absolute -top-6 -left-16 z-20 rounded-2xl p-4 shadow-xl"><div className="flex items-center gap-3"><div className="rounded-lg bg-yellow-400 p-1.5 text-white"><Star size={16} fill="currentColor" /></div><div><p className="text-xs font-bold">4.9/5 Rating</p><p className="text-muted-foreground text-[9px]">{"Trustpilot & Google"}</p></div></div></div><div className="bg-card absolute -right-8 -bottom-8 z-20 rounded-2xl p-4 shadow-xl"><div className="flex items-center gap-3"><div className="text-background rounded-lg bg-green-600 p-1.5"><ShieldCheck size={16} /></div><div><p className="text-xs font-bold">Secured</p><p className="text-muted-foreground text-[9px]">SSL Encrypted</p></div></div></div><AspectRatio ratio={1} className="w-full max-w-119.5 overflow-hidden rounded-[2.5rem] bg-white p-3 pb-0 shadow-2xl"><Image src={data.bannerImage} alt={data.imageAltText} className="transform rounded-[2.5rem] object-contain transition-transform duration-700 hover:scale-[1.03]" fetchPriority="high" loading="eager" quality={75} fill={true} sizes="(max-width: 768px) 100vw, 478px" /></AspectRatio></div>;
    $[23] = data.bannerImage;
    $[24] = data.imageAltText;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] !== t19) {
    t20 = <div className="relative col-span-12 hidden items-center justify-center md:col-span-5 md:flex">{t19}</div>;
    $[26] = t19;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== t18 || $[29] !== t20) {
    t21 = <div className="grid grid-cols-12 items-center gap-5 sm:min-h-150">{t18}{t20}</div>;
    $[28] = t18;
    $[29] = t20;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  let t22;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Text as="div" className="text-muted-foreground mb-4 text-center text-[10px] font-bold lg:text-sm">{"Partnering with World's Leading Governing Bodies"}</Text>;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  let t23;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="relative z-10 mt-6 w-full overflow-hidden">{t22}<Suspense fallback={<AccreditationMarqueeLoader />}><AccreditationMarquee /></Suspense></div>;
    $[32] = t23;
  } else {
    t23 = $[32];
  }
  let t24;
  if ($[33] !== section.sectionId || $[34] !== t2 || $[35] !== t21) {
    t24 = <Section id={t1} className={t2}>{t21}{t23}</Section>;
    $[33] = section.sectionId;
    $[34] = t2;
    $[35] = t21;
    $[36] = t24;
  } else {
    t24 = $[36];
  }
  return t24;
};
export default Banner;
function _temp(src, i) {
  return <Avatar key={i} className="relative aspect-square size-8 overflow-hidden rounded-full sm:size-10"><Image src={src} fill={true} alt={`learners-${i}`} className="w-full object-cover" sizes="40px" /><AvatarFallback className="size-8 sm:size-10">CN</AvatarFallback></Avatar>;
}